import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import {
  ServicesContainer,
  ServicesHero,
  Container,
  PageTitle,
  PageSubtitle,
  ServicesContent,
  ServicesGrid,
  ServiceCard,
  ServiceImage,
  ServiceContent,
  ServiceTitle,
  ServiceDescription,
  ServiceFeatures,
  ProcessSection,
  SectionTitle,
  SectionSubtitle,
  ProcessGrid,
  ProcessStep,
  StepNumber,
  StepTitle,
  StepDescription,
  CTASection,
  CTATitle,
  CTAText,
  CTAButton
} from '../styles/ServicesStyles';

const Services = () => {
  const navigate = useNavigate();
  const [servicesContent, setServicesContent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const services = await apiService.getServicesContent();
        setServicesContent(services);
      } catch (error) {
        console.error('Error fetching services content:', error);
        // Set default content if API fails
        setServicesContent([
          {
            name: 'Cocoa Processing',
            description: 'Professional cocoa bean processing with state-of-the-art equipment and quality control systems.',
            features: [
              'Bean cleaning and sorting',
              'Fermentation monitoring',
              'Drying and storage',
              'Quality testing and certification'
            ],
            icon: '🌱'
          },
          {
            name: 'Agricultural Consulting',
            description: 'Expert consulting services for farmers and agricultural businesses to optimize their operations.',
            features: [
              'Crop management advice',
              'Soil analysis and recommendations',
              'Pest and disease control',
              'Harvest optimization'
            ],
            icon: '🔬'
          },
          {
            name: 'Quality Control',
            description: 'Comprehensive quality assurance and testing services for agricultural products.',
            features: [
              'Laboratory testing',
              'Certification services',
              'Standards compliance',
              'Quality documentation'
            ],
            icon: '✅'
          },
          {
            name: 'Export Services',
            description: 'Complete export facilitation services for international cocoa trade.',
            features: [
              'Export documentation',
              'Logistics coordination',
              'International standards compliance',
              'Market access support'
            ],
            icon: '🚢'
          },
          {
            name: 'Training & Development',
            description: 'Educational programs for farmers and agricultural professionals.',
            features: [
              'Best practices training',
              'Technical workshops',
              'Certification programs',
              'Ongoing support'
            ],
            icon: '📚'
          },
          {
            name: 'Equipment & Technology',
            description: 'Modern agricultural equipment and technology solutions.',
            features: [
              'Equipment rental and sales',
              'Technology implementation',
              'Maintenance services',
              'Technical support'
            ],
            icon: '⚙️'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const processSteps = [
    {
      number: '1',
      title: 'Consultation',
      description: 'We begin with a thorough consultation to understand your specific needs and requirements.'
    },
    {
      number: '2',
      title: 'Assessment',
      description: 'Our experts conduct a comprehensive assessment of your current situation and challenges.'
    },
    {
      number: '3',
      title: 'Planning',
      description: 'We develop a customized plan tailored to your specific goals and constraints.'
    },
    {
      number: '4',
      title: 'Implementation',
      description: 'Our team works with you to implement the solution with minimal disruption to your operations.'
    },
    {
      number: '5',
      title: 'Monitoring',
      description: 'We provide ongoing monitoring and support to ensure optimal results and continuous improvement.'
    }
  ];

  const handleContactUs = () => {
    navigate('/contact');
  };

  if (loading) {
    return (
      <ServicesContainer>
        <Container>
          <div style={{ padding: '100px 0', textAlign: 'center' }}>
            <h2>Loading...</h2>
          </div>
        </Container>
      </ServicesContainer>
    );
  }

  return (
    <ServicesContainer>
      {/* Hero Section */}
      <ServicesHero>
        <Container>
          <PageTitle>Our Services</PageTitle>
          <PageSubtitle>
            Comprehensive solutions for all your cocoa processing and agricultural needs
          </PageSubtitle>
        </Container>
      </ServicesHero>

      {/* Services Content */}
      <ServicesContent>
        <Container>
          <ServicesGrid>
            {servicesContent.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceImage>
                  {service.icon || '🏭'}
                </ServiceImage>
                <ServiceContent>
                  <ServiceTitle>{service.name}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                  {service.features && (
                    <ServiceFeatures>
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex}>{feature}</li>
                      ))}
                    </ServiceFeatures>
                  )}
                </ServiceContent>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Container>
      </ServicesContent>

      {/* Process Section */}
      <ProcessSection>
        <Container>
          <SectionTitle>Our Process</SectionTitle>
          <SectionSubtitle>
            How we work with you to deliver exceptional results
          </SectionSubtitle>
          
          <ProcessGrid>
            {processSteps.map((step, index) => (
              <ProcessStep key={index}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </ProcessStep>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      {/* Call to Action Section */}
      <CTASection>
        <Container>
          <CTATitle>Ready to Get Started?</CTATitle>
          <CTAText>
            Contact us today to discuss how our services can help your business thrive 
            in the competitive agricultural market.
          </CTAText>
          <CTAButton onClick={handleContactUs}>
            Contact Us Today
          </CTAButton>
        </Container>
      </CTASection>
    </ServicesContainer>
  );
};

export default Services;
